import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { useToast } from '@ui/components/common/toast';
import { useApiErrorMessage } from '@website/hooks/useApiErrorMessage';
import {
  Slot,
  UpdateSlotRequest,
  archiveSlot as archiveSlotService,
  deleteSlot as deleteSlotService,
  restoreSlot as restoreSlotService,
  updateSlot as updateSlotService
} from '@website/services/slotManagement.service';

interface ToastMessages {
  successTitle?: string;
  successDescription?: string;
  errorTitle: string;
  errorDescription: string;
}

/** Shared cache invalidation after a slot mutation. */
function invalidateSlotCaches(queryClient: ReturnType<typeof useQueryClient>, activityId?: string) {
  if (activityId) {
    queryClient.invalidateQueries({ queryKey: ['activity', activityId] });
  } else {
    queryClient.invalidateQueries({ queryKey: ['activity'] });
  }
  queryClient.invalidateQueries({ queryKey: ['professional-activities'] });
  queryClient.invalidateQueries({ queryKey: ['activities'] });
  queryClient.invalidateQueries({ queryKey: ['user-activities'] });
  queryClient.invalidateQueries({ queryKey: ['available-slots'] });
  queryClient.invalidateQueries({ queryKey: ['activitySlots'] });
  queryClient.removeQueries({ queryKey: ['available-slots-all'] });
}

/**
 * Hook for deleting a slot (rule).
 */
export function useSlotDelete(toastMessages?: ToastMessages) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { success, error } = useToast();
  const apiErrorMessage = useApiErrorMessage();

  return useMutation<Slot | null, Error, { slotId: string; locale?: string }>({
    mutationFn: ({ slotId }) => deleteSlotService(slotId),
    onSuccess: (data) => {
      success(
        toastMessages?.successTitle || 'Slot Deleted',
        toastMessages?.successDescription || 'The slot has been deleted successfully.'
      );

      invalidateSlotCaches(queryClient, data?.activityId);
      router.refresh();
    },
    onError: (err) => {
      error(
        toastMessages?.errorTitle || 'Error',
        apiErrorMessage(err, toastMessages?.errorDescription || 'Failed to delete slot. Please try again.', {
          serverReason: true
        })
      );
    }
  });
}

/**
 * Hook for archiving a slot (hide + stop new bookings, keep existing).
 */
export function useSlotArchive(toastMessages?: ToastMessages) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { success, error } = useToast();
  const apiErrorMessage = useApiErrorMessage();

  return useMutation<Slot | null, Error, { slotId: string; locale?: string }>({
    mutationFn: ({ slotId }) => archiveSlotService(slotId),
    onSuccess: (data) => {
      success(
        toastMessages?.successTitle || 'Slot Archived',
        toastMessages?.successDescription || 'The slot is hidden. Existing bookings are kept.'
      );
      invalidateSlotCaches(queryClient, data?.activityId);
      router.refresh();
    },
    onError: (err) => {
      error(
        toastMessages?.errorTitle || 'Error',
        apiErrorMessage(err, toastMessages?.errorDescription || 'Failed to archive slot. Please try again.', {
          serverReason: true
        })
      );
    }
  });
}

/**
 * Hook for restoring an archived slot.
 */
export function useSlotRestore(toastMessages?: ToastMessages) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { success, error } = useToast();
  const apiErrorMessage = useApiErrorMessage();

  return useMutation<Slot | null, Error, { slotId: string; locale?: string }>({
    mutationFn: ({ slotId }) => restoreSlotService(slotId),
    onSuccess: (data) => {
      success(
        toastMessages?.successTitle || 'Slot Restored',
        toastMessages?.successDescription || 'The slot is active again.'
      );
      invalidateSlotCaches(queryClient, data?.activityId);
      router.refresh();
    },
    onError: (err) => {
      error(
        toastMessages?.errorTitle || 'Error',
        apiErrorMessage(err, toastMessages?.errorDescription || 'Failed to restore slot. Please try again.', {
          serverReason: true
        })
      );
    }
  });
}

/**
 * Hook for updating a slot (rule).
 */
export function useSlotCrudUpdate(toastMessages?: ToastMessages) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { error } = useToast();
  const apiErrorMessage = useApiErrorMessage();

  return useMutation<
    Slot | null,
    Error,
    {
      slotId: string;
      updates: UpdateSlotRequest;
      fromDate?: string;
      bookedStrategy?: 'move' | 'leave';
      locale?: string;
    }
  >({
    mutationFn: ({ slotId, updates, fromDate, bookedStrategy }) =>
      updateSlotService(slotId, updates, fromDate, bookedStrategy),
    onSuccess: (data, variables) => {
      // Success toast intentionally suppressed — the edit is reflected in the
      // calendar/list directly; only failures surface a toast.
      invalidateSlotCaches(queryClient, data?.activityId);
      queryClient.invalidateQueries({ queryKey: ['slot', variables.slotId] });
      router.refresh();
    },
    onError: (err) => {
      // Surface the API's business-rule reason (e.g. capacity floor, range flip
      // with bookings) so a blocked edit explains itself instead of a generic retry.
      error(
        toastMessages?.errorTitle || 'Error',
        apiErrorMessage(err, toastMessages?.errorDescription || 'Failed to update slot. Please try again.', {
          serverReason: true
        })
      );
    }
  });
}
